import { useRef, type JSX, type KeyboardEvent } from "react";
import { AppsListDetailRegular, FolderRegular, ClipboardTaskRegular, PersonRegular } from "@fluentui/react-icons";
import type { WorkflowTabId } from "@/types/models";
import "./TabStrip.scss";

export interface TabDefinition {
  id: WorkflowTabId;
  label: string;
  icon: JSX.Element;
}

export const WORKFLOW_TABS: TabDefinition[] = [
  { id: "quick-actions", label: "Quick Actions", icon: <AppsListDetailRegular /> },
  { id: "deals", label: "Deals", icon: <FolderRegular /> },
  { id: "tasks", label: "Tasks", icon: <ClipboardTaskRegular /> },
  { id: "profile", label: "Profile", icon: <PersonRegular /> },
];

export interface TabStripProps {
  active: WorkflowTabId;
  onChange: (tab: WorkflowTabId) => void;
}

export function workflowTabId(tab: WorkflowTabId): string {
  return `workflow-tab-${tab}`;
}

export function workflowPanelId(tab: WorkflowTabId): string {
  return `workflow-panel-${tab}`;
}

export function TabStrip({ active, onChange }: TabStripProps): JSX.Element {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectAndFocus = (index: number): void => {
    const next = WORKFLOW_TABS[index];
    if (!next) return;
    onChange(next.id);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (index + 1) % WORKFLOW_TABS.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (index - 1 + WORKFLOW_TABS.length) % WORKFLOW_TABS.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = WORKFLOW_TABS.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    selectAndFocus(nextIndex);
  };

  return (
    <div className="tab-strip" role="tablist" aria-label="TRIYO Workflow sections">
      {WORKFLOW_TABS.map((tab, index) => {
        const isActive = tab.id === active;
        return (
          <button
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            key={tab.id}
            id={workflowTabId(tab.id)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={workflowPanelId(tab.id)}
            aria-label={tab.label}
            title={tab.label}
            tabIndex={isActive ? 0 : -1}
            className={`tab-strip__tab${isActive ? " tab-strip__tab--active" : ""}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className="tab-strip__icon">{tab.icon}</span>
            {isActive ? <span className="tab-strip__indicator" aria-hidden /> : null}
          </button>
        );
      })}
    </div>
  );
}
